import { Component, ElementRef, ViewChild, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ThemeToggleButtonComponent } from '../../../../shared/components/common/theme-toggle/theme-toggle-button.component';
import { UserDropdownComponent } from '../../../../shared/components/header/user-dropdown/user-dropdown.component';
import { DropdownComponent } from '../../../../shared/components/ui/dropdown/dropdown.component';
import { BrowserPlatformService } from '../../../../shared/services/browser-platform.service';
import { ErrorAlertDebugService } from '../../../../shared/services/error-alert-debug.service';
import { SidebarService } from '../../../services/sidebar.service';
import { AuthService, SYSTEM_SECTIONS } from '../../../services/auth.service';
import { LaunchPhaseService } from '../../../services/launch-phase.service';
import { OutlookAppointmentPreferenceService } from '../../../services/outlook-appointment-preference.service';
import { EmailMailboxApi } from '../../../proxies/email-mailbox.proxy';
import { environment } from '../../../../../environments/environment';

export function getHeaderBackgroundClass(environmentName: string): string {
  switch (environmentName) {
    case 'dev':
      return 'bg-red-600 dark:bg-red-700';
    case 'uat':
      return 'bg-orange-500 dark:bg-orange-600';
    default:
      return 'bg-white dark:bg-gray-900';
  }
}

@Component({
  selector: 'app-header',
  imports: [
    CommonModule,
    RouterModule,
    DropdownComponent,
    ThemeToggleButtonComponent,
    UserDropdownComponent,
  ],
  templateUrl: './app-header.component.html',
})
export class AppHeaderComponent {
  private auth = inject(AuthService);
  private browser = inject(BrowserPlatformService);
  readonly sidebar = inject(SidebarService);
  readonly errorAlertDebug = inject(ErrorAlertDebugService);
  readonly launchPhases = inject(LaunchPhaseService);
  readonly outlookAppointments = inject(OutlookAppointmentPreferenceService);
  readonly headerBackgroundClass = getHeaderBackgroundClass(environment.name);
  private readonly emailMailboxApi = inject(EmailMailboxApi);

  isDesktopHeader = this.isDesktopViewport();
  isTestingMenuOpen = false;
  readonly emailMailboxEnabled = signal(false);
  readonly canReadCalendar = computed(() => this.auth.canReadSection(SYSTEM_SECTIONS.calendar));
  readonly canReadEmailInbox = computed(() => this.auth.canReadSection(SYSTEM_SECTIONS.emailInbox));
  readonly canShowTestingControls = computed(() => this.auth.user()?.isDcs === true);
  readonly isImpersonating = this.auth.isImpersonating;
  readonly originalUserName = this.auth.originalUserName;
  readonly returningToOriginal = this.auth.returningToOriginal;
  readonly impersonationError = this.auth.impersonationError;
  private emailMailboxStatusLoaded = false;

  private readonly syncTestingControlsAvailability = effect(() => {
    const isAvailable = this.canShowTestingControls();
    this.errorAlertDebug.setAvailable(isAvailable);
    this.outlookAppointments.setAvailable(isAvailable);
    if (!isAvailable) {
      this.closeTestingMenu();
    }
  });

  private readonly syncEmailMailboxAvailability = effect(() => {
    if (!this.canReadEmailInbox()) {
      this.emailMailboxStatusLoaded = false;
      this.emailMailboxEnabled.set(false);
      return;
    }

    if (this.emailMailboxStatusLoaded) {
      return;
    }

    this.emailMailboxStatusLoaded = true;
    this.emailMailboxApi.getStatus().subscribe({
      next: (status) => {
        if (this.canReadEmailInbox()) {
          this.emailMailboxEnabled.set(status.enabled);
        }
      },
      error: () => this.emailMailboxEnabled.set(false),
    });
  });

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  onResize() {
    const isDesktop = this.isDesktopViewport();
    if (this.isDesktopHeader !== isDesktop) {
      this.isDesktopHeader = isDesktop;
      this.closeTestingMenu();
    }
  }

  toggleMobileSidebar() {
    this.sidebar.toggleMobileOpen();
  }

  toggleTestingMenu() {
    this.isTestingMenuOpen = !this.isTestingMenuOpen;
  }

  closeTestingMenu() {
    this.isTestingMenuOpen = false;
  }

  toggleTestErrors() {
    this.errorAlertDebug.toggleShowTestErrors();
  }

  toggleKeepSidebarExpanded() {
    this.sidebar.toggleKeepExpandedAfterNavigation();
  }

  toggleOutlookAppointments() {
    this.outlookAppointments.toggleProcessChanges();
  }

  toggleLocalLaunchPhaseMode() {
    this.launchPhases.toggleLocalPhaseMode();
  }

  prepareCalendarTabSession() {
    this.auth.prepareSessionForNewTab();
  }

  returnToOriginalUser() {
    this.auth.returnToOriginalUser();
  }

  ngAfterViewInit() {
    this.browser.addDocumentEventListener('keydown', this.handleKeyDown);
    this.browser.addWindowEventListener('resize', this.handleResize);
  }

  ngOnDestroy() {
    this.browser.removeDocumentEventListener('keydown', this.handleKeyDown);
    this.browser.removeWindowEventListener('resize', this.handleResize);
  }

  private handleResize = () => this.onResize();

  handleKeyDown = (event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.searchInput?.nativeElement.focus();
    }
  };

  private isDesktopViewport() {
    return this.browser.innerWidth >= 1280;
  }
}
